import React from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import { SEMANTIC_HR_CONVERSATION } from '../../../../content/enterpriseMessaging';
import StoryMedia from '../../shared/StoryMedia';
import SemanticHRConversationVisual from './SemanticHRConversationVisual';
import { AgenticWorkflowSteps } from './AgenticWorkflow';

const complexityStyles = {
  Simple: 'badge-neutral',
  Focused: 'badge-brand',
  Connected: 'badge-brand',
  'Plan vs actual': 'badge-accent',
  Strategic: 'badge-navy',
  Executive: 'badge-brand',
};

export function ConversationDemoTimeline({ className = '' }) {
  const copy = SEMANTIC_HR_CONVERSATION;

  return (
    <div className={`flex flex-col items-end text-right ${className}`}>
      <div className="mb-8 max-w-lg w-full">
        <p className="eyebrow text-right">{copy.demoEyebrow}</p>
        <h3 className="mt-3 text-2xl sm:text-3xl font-semibold tracking-tight text-ink-900 text-right">
          {copy.demoTitle}
        </h3>
        <p className="mt-4 text-base leading-relaxed text-ink-600 text-right">{copy.demoIntro}</p>
      </div>

      <div className="relative w-full max-w-lg ml-auto">
        <div className="absolute right-4 top-4 bottom-4 w-px bg-brand-200 hidden sm:block" aria-hidden="true" />

        <ol className="space-y-8">
          {copy.turns.map((turn) => (
            <li key={turn.turn} className="relative sm:pr-12">
              <span className="hidden sm:flex absolute right-0 top-5 h-8 w-8 items-center justify-center rounded-full bg-brand-700 text-white text-xs font-semibold z-10">
                {turn.turn}
              </span>

              <article className="card p-6 md:p-7 ml-auto max-w-lg">
                <div className="flex flex-wrap items-center justify-end gap-2 mb-4">
                  <span className="sm:hidden text-xs font-mono font-semibold text-brand-500">
                    Turn {turn.turn}
                  </span>
                  <span className="text-xs font-semibold text-ink-400 uppercase tracking-wide">
                    {turn.label}
                  </span>
                  <span
                    className={`text-xs font-medium rounded-full px-2.5 py-0.5 ${
                      complexityStyles[turn.complexity] || 'badge-neutral'
                    }`}
                  >
                    {turn.complexity}
                  </span>
                </div>

                <div className="space-y-4">
                  <div>
                    <p className="text-xs font-semibold text-ink-500 uppercase tracking-wide mb-1.5 text-right">
                      Question
                    </p>
                    <div className="rounded-lg border border-ink-200 bg-surface-muted px-4 py-3 text-sm text-ink-800 leading-relaxed text-left">
                      &ldquo;{turn.question}&rdquo;
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-semibold text-ink-500 uppercase tracking-wide mb-1.5 text-right">
                      What happens
                    </p>
                    <p className="text-sm text-ink-600 leading-relaxed text-right">{turn.response}</p>
                  </div>

                  <StoryMedia variant="minimal">
                    <p className="text-sm font-medium text-brand-700 text-right">{turn.insight}</p>
                  </StoryMedia>
                </div>
              </article>
            </li>
          ))}
        </ol>
      </div>

      <div className="mt-10 rounded-xl border border-ink-200 bg-white/80 px-6 py-5 ml-auto max-w-lg w-full">
        <p className="text-sm font-semibold text-ink-900 mb-1 text-right">{copy.footerTitle}</p>
        <p className="text-sm text-ink-600 leading-relaxed text-right">{copy.footerBody}</p>
      </div>
    </div>
  );
}

export default function ConversationStory() {
  const copy = SEMANTIC_HR_CONVERSATION;

  return (
    <section className="section-tight pb-10 md:pb-12">
      <div className="container-x">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] gap-8 lg:gap-10 lg:items-stretch pb-10 lg:pb-12 mb-10 lg:mb-12 border-b border-ink-200/80">
          <div>
            <p className="eyebrow">{copy.eyebrow}</p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-ink-900">
              {copy.title}
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-ink-600">{copy.intro}</p>

            <div className="mt-10 pt-10 border-t border-ink-200/80">
              <p className="eyebrow">{copy.semanticEyebrow}</p>
              <h3 className="mt-3 text-2xl sm:text-3xl font-semibold tracking-tight text-ink-900">
                {copy.semanticTitle}
              </h3>
              <p className="mt-4 text-base leading-relaxed text-ink-600">{copy.semanticIntro}</p>
              <ul className="mt-6 space-y-3">
                {copy.semanticPoints.map((point) => (
                  <li key={point} className="flex items-start gap-3 text-sm leading-relaxed text-ink-600">
                    <CheckIcon className="mt-0.5 h-5 w-5 shrink-0 text-brand-500" aria-hidden="true" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="flex w-full min-w-0 items-center justify-center lg:sticky lg:top-24 lg:self-stretch">
            <SemanticHRConversationVisual className="w-full" />
          </div>
        </div>

        <div className="text-left">
          <p className="eyebrow">Agentic Architecture</p>
          <h3 className="mt-3 text-2xl sm:text-3xl font-semibold tracking-tight text-ink-900">
            Analytics That Move at the Speed of Conversation
          </h3>
          <p className="mt-4 text-base leading-relaxed text-ink-600">
            Traditional workforce analytics stalls on ticket queues and static dashboards.
            Semantic HR activates the right pre-approved view for every question, with
            filters set from context.
          </p>
          <AgenticWorkflowSteps className="mt-8" />
        </div>

        <div className="mt-16 md:mt-20 border-t border-slate-200/80 pt-14 md:pt-16">
          <ConversationDemoTimeline />
        </div>
      </div>
    </section>
  );
}
